import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, Kbd } from "@/design";
import { adminNav } from "./adminNav";
import { useProjectSlug, useWorkspaceSlug } from "./context";
import { projectNav } from "./nav";

const OPEN = "guv:shortcuts";
export function openShortcuts(): void {
  window.dispatchEvent(new Event(OPEN));
}

function isTyping(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  return !!t && (t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName));
}

/**
 * Global keyboard shortcuts: ? for help, g then a letter to jump between project pages.
 * In the platform admin console (`admin`), g then a letter jumps between admin pages instead.
 */
export function Shortcuts({ admin = false }: { admin?: boolean }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const workspace = useWorkspaceSlug();
  const project = useProjectSlug();
  const pendingG = useRef<number>(0);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "?") {
        e.preventDefault();
        setOpen(true);
        return;
      }
      if (e.key === "g") {
        pendingG.current = Date.now();
        return;
      }
      if (Date.now() - pendingG.current < 1200) {
        pendingG.current = 0;
        if (admin) {
          const a = adminNav.find((n) => n.key2 === e.key);
          if (a) void navigate({ to: a.to });
          return;
        }
        if (e.key === "h") {
          void navigate({ to: "/$workspace", params: { workspace } });
          return;
        }
        const item = project && projectNav.find((n) => n.key2 === e.key);
        if (item) void navigate({ to: item.to, params: { workspace, project } as never, search: ((s: Record<string, unknown>) => s) as never });
      }
    };
    window.addEventListener(OPEN, onOpen);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener(OPEN, onOpen);
      window.removeEventListener("keydown", onKey);
    };
  }, [navigate, workspace, project, admin]);

  const go = (key2: string): React.ReactNode => <span className="flex gap-1"><Kbd>G</Kbd><Kbd>{key2.toUpperCase()}</Kbd></span>;
  const rows: [React.ReactNode, string][] = [
    [<Kbd mod>K</Kbd>, "Search and command palette"],
    [<Kbd>?</Kbd>, "Show this list"],
    ...(admin
      ? adminNav.map((n): [React.ReactNode, string] => [go(n.key2), `Go to ${n.label}`])
      : [[go("h"), "Go to projects"] as [React.ReactNode, string], ...projectNav.map((n): [React.ReactNode, string] => [go(n.key2), `Go to ${n.label}`])]),
  ];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent title="Keyboard shortcuts" description={admin ? "Guv is keyboard-first. These work anywhere in the admin console." : "Guv is keyboard-first. Project shortcuts work when a project is open."} size="md">
        <dl className="divide-y divide-border">
          {rows.map(([keys, label], i) => (
            <div key={i} className="flex items-center justify-between py-2.5">
              <dt className="text-base text-text">{label}</dt>
              <dd>{keys}</dd>
            </div>
          ))}
        </dl>
      </DialogContent>
    </Dialog>
  );
}
